import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import {
  getParticipantGridMaxVisibleTileCount,
  getParticipantGridViewportBreakpoints
} from "./participant-grid-capacity";
import { getParticipantLayout } from "./participant-layout-utils";
import {
  PARTICIPANT_TILE_KIND,
  getEffectiveParticipantLayoutMode,
  getRenderableParticipantLayoutParticipants
} from "./participant-layout-participants";
import { useParticipantLayoutDialogAudioActivityState } from "./useParticipantLayoutDialogAudioActivityState";
import { ParticipantBackdrop } from "./ParticipantBackdrop";
import { ParticipantSpotlightLayout } from "./ParticipantSpotlightLayout";
import { ParticipantGridLayout } from "./ParticipantGridLayout";
import { ParticipantReactionFeed } from "./ParticipantReactionFeed";
import { ParticipantLayoutPagination } from "./ParticipantLayoutPagination";
import { clampParticipantLayoutPageIndex, getParticipantLayoutPage } from "./participant-layout-pagination";

const useStyles = makeStyles({
  root: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    pointerEvents: "auto"
  },
  content: {
    position: "absolute",
    inset: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
    minHeight: 0,
    paddingTop: "70px",
    paddingRight: "12px",
    paddingBottom: "65px",
    paddingLeft: "12px",
    pointerEvents: "none",
    "@media (max-width: 991px)": {
      gap: tokens.spacingVerticalXS
    },
    "@media (max-height: 459px)": {
      paddingTop: "12px",
      paddingBottom: "12px"
    },
    "@media (max-height: 260px)": {
      gap: tokens.spacingVerticalXS,
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  spotlightContent: {
    "@media (max-width: 640px)": {
      paddingTop: "70px",
      paddingRight: "8px",
      paddingBottom: "65px",
      paddingLeft: "8px"
    },
    "@media (min-width: 641px) and (max-height: 560px)": {
      paddingTop: "70px",
      paddingRight: "8px",
      paddingBottom: "65px",
      paddingLeft: "8px"
    }
  },
  statusLayer: {
    position: "relative",
    zIndex: 10,
    boxSizing: "border-box",
    display: "flex",
    flex: "0 0 auto",
    justifyContent: "flex-end",
    width: "100%",
    minWidth: 0,
    pointerEvents: "none"
  },
  layoutLayer: {
    position: "relative",
    zIndex: 1,
    flex: "1 1 auto",
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    pointerEvents: "auto"
  },
  paginationLayer: {
    display: "flex",
    flex: "0 0 auto",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    pointerEvents: "none"
  }
});

function noop() {}

function getParticipantLayoutViewportSize() {
  if (typeof window === "undefined") {
    return {
      width: 1200,
      height: 900
    };
  }

  return {
    width: window.innerWidth || 1200,
    height: window.innerHeight || 900
  };
}

function useParticipantLayoutViewportSize(enabled) {
  const [viewportSize, setViewportSize] = useState(getParticipantLayoutViewportSize);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return undefined;
    }

    const syncViewportSize = () => {
      setViewportSize(getParticipantLayoutViewportSize());
    };

    syncViewportSize();
    window.addEventListener("resize", syncViewportSize);

    return () => {
      window.removeEventListener("resize", syncViewportSize);
    };
  }, [enabled]);

  return viewportSize;
}

export function getParticipantLayoutStatusRowReservedHeight() {
  return 0;
}

export function getParticipantLayoutSpeakingSampleParticipantIds(participants = []) {
  const sampledParticipantIds = [];
  const sampledParticipantIdSet = new Set();

  participants.forEach(participant => {
    if (participant?.kind !== PARTICIPANT_TILE_KIND || !participant.id || sampledParticipantIdSet.has(participant.id)) {
      return;
    }

    sampledParticipantIds.push(participant.id);
    sampledParticipantIdSet.add(participant.id);
  });

  return sampledParticipantIds;
}

function applyParticipantLayoutSpeakingState(participant, speakingBySessionId) {
  if (participant.kind !== PARTICIPANT_TILE_KIND) {
    return participant;
  }

  const isSpeaking = Boolean(speakingBySessionId[participant.id]) && !participant.isMuted;

  if (participant.isSpeaking === isSpeaking) {
    return participant;
  }

  return {
    ...participant,
    isSpeaking
  };
}

export function ParticipantLayout({
  enabled = true,
  mode = "auto",
  participants = [],
  showParticipants = true,
  mySessionId,
  zoomPinnedParticipant = false,
  onRequestParticipantMute,
  onToggleParticipantMuted = noop,
  onToggleParticipantPinned = noop,
  onToggleParticipantRoomSpotlight
}) {
  const styles = useStyles();
  const [gridPageIndex, setGridPageIndex] = useState(0);
  const [zoomedParticipantId, setZoomedParticipantId] = useState(null);
  const visibleParticipants = useMemo(() => (showParticipants ? participants : []), [participants, showParticipants]);
  const effectiveMode = getEffectiveParticipantLayoutMode(mode, visibleParticipants);
  const viewportSize = useParticipantLayoutViewportSize(enabled);
  const hasVisibleParticipants = visibleParticipants.length > 0;
  const hasStatusTray = false;
  const pinnedParticipant = visibleParticipants.find(participant => participant.isPinned) || null;
  const hasPinnedParticipant = Boolean(pinnedParticipant);
  const isPinnedZoomActive =
    hasPinnedParticipant && (zoomPinnedParticipant || zoomedParticipantId === pinnedParticipant?.id);
  const layoutViewportHeight = Math.max(
    0,
    viewportSize.height -
      getParticipantLayoutStatusRowReservedHeight({
        hasStatusTray,
        viewportHeight: viewportSize.height,
        viewportWidth: viewportSize.width
      })
  );
  const viewportBreakpoints = useMemo(
    () =>
      getParticipantGridViewportBreakpoints({
        width: viewportSize.width,
        height: layoutViewportHeight
      }),
    [layoutViewportHeight, viewportSize.width]
  );
  const maxVisibleGridTiles = useMemo(
    () =>
      effectiveMode !== "spotlight"
        ? getParticipantGridMaxVisibleTileCount({
            participantCount: visibleParticipants.length,
            viewportWidth: viewportSize.width,
            viewportHeight: layoutViewportHeight
          })
        : undefined,
    [effectiveMode, layoutViewportHeight, visibleParticipants.length, viewportSize.width]
  );
  const gridPagination = useMemo(
    () =>
      effectiveMode !== "spotlight" && maxVisibleGridTiles
        ? getParticipantLayoutPage({
            items: visibleParticipants,
            pageIndex: gridPageIndex,
            pageSize: maxVisibleGridTiles
          })
        : null,
    [effectiveMode, gridPageIndex, maxVisibleGridTiles, visibleParticipants]
  );
  const layoutParticipants = gridPagination ? gridPagination.items : visibleParticipants;
  const hasGridPagination = Boolean(gridPagination);
  const gridPageCount = gridPagination?.pageCount || 1;
  const isGridPaginationActive = hasGridPagination && gridPageCount > 1;
  const layout = getParticipantLayout({
    participantCount: layoutParticipants.length,
    mode: isGridPaginationActive ? "grid" : effectiveMode,
    maxVisibleGridTiles,
    usePinnedTwoParticipantStrip: hasPinnedParticipant
  });
  const renderParticipants = getRenderableParticipantLayoutParticipants(layout, layoutParticipants);
  const speakingSampleParticipantIds = useMemo(
    () => getParticipantLayoutSpeakingSampleParticipantIds(renderParticipants),
    [renderParticipants]
  );
  const { speakingBySessionId } = useParticipantLayoutDialogAudioActivityState({
    enabled: enabled && showParticipants && Boolean(mySessionId) && speakingSampleParticipantIds.length > 0,
    mySessionId,
    participantIds: speakingSampleParticipantIds
  });
  const renderParticipantsWithSpeakingState = useMemo(
    () =>
      renderParticipants.map(participant =>
        applyParticipantLayoutSpeakingState(participant, speakingBySessionId || {})
      ),
    [renderParticipants, speakingBySessionId]
  );
  const requestParticipantMute = useCallback(
    participant => {
      if (onRequestParticipantMute) {
        onRequestParticipantMute(participant);
        return;
      }

      onToggleParticipantMuted(participant.id);
    },
    [onRequestParticipantMute, onToggleParticipantMuted]
  );
  const requestParticipantFullscreen = useCallback(participant => {
    setZoomedParticipantId(currentZoomedParticipantId =>
      currentZoomedParticipantId === participant.id ? null : participant.id
    );
  }, []);
  const setClampedGridPageIndex = useCallback(
    nextPageIndex => {
      setGridPageIndex(clampParticipantLayoutPageIndex(nextPageIndex, gridPageCount));
    },
    [gridPageCount]
  );

  useEffect(() => {
    if (!hasGridPagination) {
      setGridPageIndex(0);
      return;
    }

    setGridPageIndex(currentPageIndex => clampParticipantLayoutPageIndex(currentPageIndex, gridPageCount));
  }, [gridPageCount, hasGridPagination]);

  useEffect(() => {
    if (!hasPinnedParticipant || (zoomedParticipantId && zoomedParticipantId !== pinnedParticipant?.id)) {
      setZoomedParticipantId(null);
    }
  }, [hasPinnedParticipant, pinnedParticipant, zoomedParticipantId]);

  if (!enabled) {
    return null;
  }

  return (
    <div
      className={styles.root}
      data-participant-layout-root=""
      data-participant-layout-mode={layout.mode}
      data-participant-layout-page={gridPagination ? gridPagination.pageIndex + 1 : 1}
      data-participant-layout-page-count={gridPagination ? gridPagination.pageCount : 1}
      data-participant-layout-variant={layout.variant}
    >
      <ParticipantBackdrop />
      <div
        className={mergeClasses(styles.content, layout.mode === "spotlight" && styles.spotlightContent)}
        data-participant-layout-content=""
      >
        {hasVisibleParticipants && (
          <div className={styles.layoutLayer} data-participant-layout-stage-layer="">
            {layout.mode === "spotlight" ? (
              <ParticipantSpotlightLayout
                hasStatusTray={hasStatusTray}
                layout={layout}
                participants={renderParticipantsWithSpeakingState}
                onRequestParticipantFullscreen={requestParticipantFullscreen}
                onRequestParticipantMute={requestParticipantMute}
                onToggleParticipantMuted={onToggleParticipantMuted}
                onToggleParticipantPinned={onToggleParticipantPinned}
                onToggleParticipantRoomSpotlight={onToggleParticipantRoomSpotlight}
                zoomPinnedParticipant={isPinnedZoomActive}
              />
            ) : (
              <ParticipantGridLayout
                hasStatusTray={hasStatusTray}
                layout={layout}
                participants={renderParticipantsWithSpeakingState}
                totalParticipantCount={visibleParticipants.length}
                viewportBreakpoints={viewportBreakpoints}
                onRequestParticipantFullscreen={requestParticipantFullscreen}
                onRequestParticipantMute={requestParticipantMute}
                onSwipeNextPage={gridPageCount > 1 ? () => setClampedGridPageIndex(gridPageIndex + 1) : undefined}
                onSwipePreviousPage={gridPageCount > 1 ? () => setClampedGridPageIndex(gridPageIndex - 1) : undefined}
                onToggleParticipantMuted={onToggleParticipantMuted}
                onToggleParticipantPinned={onToggleParticipantPinned}
                onToggleParticipantRoomSpotlight={onToggleParticipantRoomSpotlight}
              />
            )}
          </div>
        )}
        {gridPagination && gridPagination.pageCount > 1 && (
          <div className={styles.paginationLayer} data-participant-layout-pagination-layer="">
            <ParticipantLayoutPagination
              onPageChange={setClampedGridPageIndex}
              pageCount={gridPagination.pageCount}
              pageIndex={gridPagination.pageIndex}
            />
          </div>
        )}
      </div>
      <ParticipantReactionFeed participants={visibleParticipants} />
    </div>
  );
}

ParticipantLayout.propTypes = {
  enabled: PropTypes.bool,
  mode: PropTypes.oneOf(["auto", "grid", "spotlight"]),
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.string,
      displayName: PropTypes.string.isRequired,
      initials: PropTypes.string.isRequired,
      isMuted: PropTypes.bool,
      isPinned: PropTypes.bool,
      isHandRaised: PropTypes.bool,
      isSelf: PropTypes.bool,
      mediaStream: PropTypes.object,
      role: PropTypes.string,
      canToggleMuted: PropTypes.bool,
      canToggleRoomSpotlight: PropTypes.bool,
      isRoomSpotlighted: PropTypes.bool,
      roomSpotlightActive: PropTypes.bool,
      hiddenParticipants: PropTypes.array,
      overflowCount: PropTypes.number
    })
  ),
  showParticipants: PropTypes.bool,
  mySessionId: PropTypes.string,
  zoomPinnedParticipant: PropTypes.bool,
  onRequestClose: PropTypes.func,
  onRequestParticipantMute: PropTypes.func,
  onToggleParticipantMuted: PropTypes.func,
  onToggleParticipantPinned: PropTypes.func,
  onToggleParticipantRoomSpotlight: PropTypes.func
};
